<template>
  <div class="onboarding-boards">
    <EntraPageHeader
      title="Onboarding Wizard"
      :showTenantInfo="false"
      :showBreadcrumb="false"
      :showSearch="false"
      :showSavedViews="false"
      :showExport="false"
      :showRefresh="true"
      @refresh="loadData"
    />

    <div class="page-body">
      <div v-if="error" class="error-message" role="alert">{{ error }}</div>

      <div class="filters-bar">
        <div class="search-wrap">
          <svg class="search-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.48l4.82 4.82a.75.75 0 11-1.06 1.06l-4.82-4.82A6 6 0 012 8z" clip-rule="evenodd"/>
          </svg>
          <input
            v-model="search"
            type="text"
            class="filter-input"
            placeholder="Search customer or DB ticket..."
            @keyup.enter="reloadList"
            aria-label="Search onboarding boards"
          />
        </div>
        <router-link to="/presales/submissions" class="ghost-link">From a submission</router-link>
      </div>

      <div v-if="loading" class="loading-state">
        <span class="spinner" aria-hidden="true"></span> Loading boards...
      </div>

      <div v-else-if="items.length === 0" class="card empty-card">
        <svg class="empty-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M10 3a7 7 0 100 14 7 7 0 000-14zM8.7 8.1a1 1 0 011.4 0L12 10l1.9-1.9a1 1 0 111.4 1.4L13.4 11.4l1.9 1.9a1 1 0 01-1.4 1.4L12 12.8l-1.9 1.9a1 1 0 01-1.4-1.4l1.9-1.9-1.9-1.9a1 1 0 010-1.4z" clip-rule="evenodd"/>
        </svg>
        <h3>No onboarding boards yet</h3>
        <p class="muted">Boards are generated from presales handover submissions via "Start onboarding".</p>
      </div>

      <div v-else class="table-wrapper card">
        <table class="data-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>DB Ticket</th>
              <th>Progress</th>
              <th>Started</th>
              <th class="col-action"><span class="visually-hidden">Open</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.id" class="row-link" @click="openBoard(item.id)">
              <td>
                <div class="cell-customer">
                  <span class="avatar avatar-sm" aria-hidden="true">{{ initialsFor(item.customerName) }}</span>
                  <span class="cell-name">{{ item.customerName }}</span>
                </div>
              </td>
              <td><code class="client-code">{{ item.dbTicket }}</code></td>
              <td>
                <div class="progress-cell">
                  <div class="mini-progress-track">
                    <div class="mini-progress-fill" :style="{ width: progressPercent(item) + '%' }"></div>
                  </div>
                  <span class="muted progress-text">{{ item.completedTasks }}/{{ item.totalTasks }}</span>
                </div>
              </td>
              <td class="muted">{{ formatDate(item.createdAt) }}</td>
              <td class="col-action">
                <router-link :to="'/presales/onboarding/' + item.id" class="view-link" @click.stop>Open</router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="totalPages > 1" class="pagination">
        <button type="button" class="secondary-button" :disabled="page <= 1" @click="changePage(page - 1)">Previous</button>
        <span class="muted">Page {{ page }} of {{ totalPages }}</span>
        <button type="button" class="secondary-button" :disabled="page >= totalPages" @click="changePage(page + 1)">Next</button>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import { getOnboardingBoards } from '@/services/presales-api.js';

export default {
  name: 'OnboardingBoards',
  components: { EntraPageHeader },
  data() {
    return {
      items: [],
      loading: false,
      error: null,
      search: '',
      page: 1,
      pageSize: 20,
      total: 0
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      this.error = null;
      try {
        const result = await getOnboardingBoards({
          page: this.page,
          pageSize: this.pageSize,
          search: this.search || undefined
        });
        this.items = result.items;
        this.total = result.total;
      } catch (error) {
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },
    reloadList() {
      this.page = 1;
      this.loadData();
    },
    changePage(page) {
      this.page = page;
      this.loadData();
    },
    openBoard(id) {
      this.$router.push('/presales/onboarding/' + id);
    },
    progressPercent(item) {
      if (!item.totalTasks) return 0;
      return Math.round((item.completedTasks / item.totalTasks) * 100);
    },
    initialsFor(name) {
      if (!name) return '?';
      return name
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join('');
    },
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString();
    }
  }
};
</script>

<style scoped>
.onboarding-boards {
  min-height: 100vh;
  background: #f8fafc;
}

.page-body {
  padding: 1.5rem 2rem 3rem;
  max-width: 1100px;
}

.filters-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.search-wrap {
  position: relative;
  flex: 1 1 320px;
  max-width: 420px;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1rem;
  height: 1rem;
  color: #94a3b8;
}

.filter-input {
  width: 100%;
  padding: 0.625rem 0.75rem 0.625rem 2.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  font-family: inherit;
  color: #1e293b;
}

.filter-input:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
}

.ghost-link {
  color: #64748b;
  font-size: 0.8125rem;
  text-decoration: none;
}

.ghost-link:hover {
  color: #4f46e5;
}

.card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.table-wrapper {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  text-align: left;
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.data-table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.875rem;
  color: #1e293b;
}

.row-link {
  cursor: pointer;
  transition: background 120ms ease;
}

.row-link:hover {
  background: #f8fafc;
}

.cell-customer {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.avatar-sm {
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 999px;
  background: #4f46e5;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.72rem;
}

.cell-name {
  font-weight: 600;
}

.client-code {
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  color: #3730a3;
  border-radius: 6px;
  padding: 0.1rem 0.45rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.progress-cell {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 140px;
}

.mini-progress-track {
  flex: 1;
  height: 6px;
  background: #eef2ff;
  border-radius: 999px;
  overflow: hidden;
}

.mini-progress-fill {
  height: 100%;
  background: #4f46e5;
  border-radius: 999px;
}

.progress-text {
  font-size: 0.75rem;
  white-space: nowrap;
}

.muted {
  color: #64748b;
}

.view-link {
  color: #4f46e5;
  font-weight: 600;
  text-decoration: none;
  font-size: 0.8125rem;
}

.view-link:hover {
  text-decoration: underline;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

.col-action {
  text-align: right;
  width: 1%;
  white-space: nowrap;
}

.empty-card {
  max-width: 480px;
  margin: 2rem auto 0;
  padding: 2.5rem;
  text-align: center;
}

.empty-icon {
  width: 2.5rem;
  height: 2.5rem;
  color: #cbd5e1;
  margin-bottom: 0.5rem;
}

.empty-card h3 {
  margin: 0 0 0.5rem;
  font-size: 1rem;
  color: #0f172a;
}

.empty-card p {
  margin: 0;
  font-size: 0.875rem;
}

.loading-state {
  color: #64748b;
  padding: 2rem 0;
}

.spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid #c7d2fe;
  border-top-color: #4f46e5;
  border-radius: 999px;
  animation: spin 600ms linear infinite;
  margin-right: 0.4rem;
  vertical-align: -2px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.secondary-button {
  background: #ffffff;
  color: #334155;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}

.secondary-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  justify-content: flex-end;
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1.25rem;
  font-size: 0.875rem;
}
</style>
